import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ZoomIn, ShieldCheck, Ruler, Clock, Truck } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import Layout from '../Layout';
import SEOHead from '../SEOHead';
import Breadcrumb from '../Breadcrumb';
import HeroSection from './HeroSection';
import FAQSection from '../FAQSection';
import { business } from '../../config/business';

// Import local images
import heroImage from '../assets/industrial-shed.jpg';
import project1 from '../assets/ind.png';
import project2 from '../assets/pre- engineered metal building(PEMB).png';
import project3 from '../assets/pre-engineered.png';
import project4 from '../assets/industrial-shed1.jpg.png';
import project5 from '../assets/2.png';
import project6 from '../assets/industrial-structure.jpg';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const process = [
  { icon: Ruler, title: "Design", text: "Structural design engineered to your span, height and load requirements." },
  { icon: ShieldCheck, title: "Fabrication", text: "Steel fabricated at our Hyderabad facility under quality control checks." },
  { icon: Truck, title: "Delivery", text: "Transport coordinated to your site across Telangana or AP." },
  { icon: Clock, title: "Erection", text: "On-site assembly through to a handover-ready structure." },
];

const faqs = [
  { q: "Can you share technical details of a completed project similar to mine?", a: "Yes — during a project consultation we can walk through relevant completed work that matches your building type and requirements." },
  { q: "Do you take on projects of any size?", a: "We work on a range of project sizes; share your requirements and we'll confirm fit and timeline." },
];

const Projects = () => {
  const [activeCategory, setActiveCategory] = useState("All");

  const projects = [
    { id: 1, image: project1, title: "Warehouse Structure", category: "Industrial" },
    { id: 2, image: project2, title: "Factory Shed", category: "Industrial" },
    { id: 3, image: project3, title: "Pre-Engineered Building", category: "PEB" },
    { id: 4, image: project4, title: "Commercial Complex", category: "Commercial" },
    { id: 5, image: project5, title: "Storage Facility", category: "Warehouse" },
    { id: 6, image: project6, title: "Manufacturing Unit", category: "Industrial" },
  ];

  const categories = ["All", "Industrial", "PEB", "Commercial", "Warehouse"];
  const filteredProjects = activeCategory === "All" ? projects : projects.filter((p) => p.category === activeCategory);

  return (
    <Layout>
      <SEOHead
        title="TR Structures Projects – Industrial & Prefab Buildings in Hyderabad"
        description="Explore TR Structures' completed projects in Hyderabad and Telangana, including industrial warehouses, commercial buildings, and prefab structures. Durable, energy-efficient, and fully customized PEB solutions."
        keywords="PEB projects Hyderabad, industrial warehouses, prefab buildings, commercial buildings Hyderabad, pre-engineered buildings, modular structures, steel structures, TR Structures, Telangana"
      />
      <Breadcrumb items={[{ name: "Projects" }]} />

      <main>
        {/* 1. Hero */}
        <HeroSection
          title="Our Projects"
          subtitle="Completed Work Across Hyderabad & Telangana"
          description="From industrial warehouses to factory sheds — we deliver strength, speed and precision in every project."
          backgroundImage={heroImage}
          primaryButtonText="Get Quote"
          primaryButtonLink="/enquiry"
          secondaryButtonText="Contact Us"
          secondaryButtonLink="/contact"
        />

        {/* 2. Trust strip */}
        <section className="bg-primary py-6">
          <div className="container-custom">
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-primary-foreground text-center">
              <span className="font-montserrat font-semibold flex items-center gap-2"><ShieldCheck size={18} aria-hidden="true" /> Site-specific structural design</span>
              <span className="font-montserrat font-semibold flex items-center gap-2"><Ruler size={18} aria-hidden="true" /> Precision fabrication</span>
              <span className="font-montserrat font-semibold flex items-center gap-2"><Clock size={18} aria-hidden="true" /> On-time erection</span>
            </div>
          </div>
        </section>

        {/* 3. Filters + Grid */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-5 py-2.5 rounded-full font-montserrat font-semibold text-sm transition-all shadow-sm ${
                    activeCategory === cat
                      ? "bg-primary text-primary-foreground scale-105"
                      : "bg-card text-foreground hover:bg-primary/10"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              transition={{ staggerChildren: 0.08 }}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredProjects.map((project) => (
                <motion.div
                  key={project.id}
                  variants={fadeUp}
                  className="group relative rounded-2xl overflow-hidden shadow-lg card-hover cursor-pointer"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-72 object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-secondary/85 opacity-0 group-hover:opacity-100 transition-opacity duration-400 flex items-center justify-center">
                      <ZoomIn size={48} className="text-white" strokeWidth={3} aria-hidden="true" />
                    </div>
                  </div>
                  <div className="p-5 bg-card text-center">
                    <h3 className="font-montserrat text-xl font-bold text-foreground">{project.title}</h3>
                    <p className="text-muted-foreground text-sm mt-2">{project.category} • Hyderabad</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* 4. Process */}
        <section className="section-padding bg-muted">
          <div className="container-custom">
            <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-foreground mb-10 text-center">
              How We Deliver Every Project
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {process.map((step, i) => (
                <div key={step.title} className="p-6 bg-card rounded-xl shadow-md text-center card-hover">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <step.icon className="text-primary" size={22} aria-hidden="true" />
                  </div>
                  <p className="text-xs font-montserrat font-bold text-secondary mb-2">STEP {i + 1}</p>
                  <h3 className="font-montserrat font-bold text-foreground mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 5. Cross-links */}
        <section className="section-padding">
          <div className="container-custom text-center">
            <h2 className="font-montserrat text-2xl font-bold text-foreground mb-8">Explore by Industry</h2>
            <Link to="/industries" className="btn-outline inline-block px-8 py-3">
              View Industries We Serve
            </Link>
          </div>
        </section>

        {/* 6. FAQ */}
        <FAQSection faqs={faqs} />

        {/* 7. CTA */}
        <section className="section-padding bg-secondary">
          <div className="container-custom text-center">
            <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-secondary-foreground mb-4">
              Start a Similar Project
            </h2>
            <p className="text-secondary-foreground/90 mb-8 max-w-xl mx-auto">
              Tell us what you're building and we'll follow up with a design consultation and estimate.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/enquiry" className="btn-primary inline-block px-8 py-4">
                Get Project Estimate
              </Link>
              <a href={business.whatsapp.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-lg font-montserrat font-semibold hover:bg-[#20bd5a] transition">
                <FaWhatsapp size={20} aria-hidden="true" /> WhatsApp Us
              </a>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
};

export default Projects;
